import { Command } from 'cmdk';
import { Check, ChevronDown, Plus, Search } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Chip } from './chip';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export type MultiSelectOption = {
  value: string;
  /** Plain text: used for search and as the accessible name. */
  label: string;
  /** Leading visual, e.g. <NetworkLogo size={16} />. */
  icon?: ReactNode;
  /** Secondary text, e.g. "com.lemon.runner · Android". */
  meta?: string;
  /** Listed but not selectable; say why in `meta` (e.g. "Private · invite the app first"). */
  disabled?: boolean;
};

type MultiSelectProps = {
  /** The field name shown on the trigger and naming the popover ("Network"). */
  label: string;
  options: readonly MultiSelectOption[];
  value: readonly string[];
  onChange: (value: string[]) => void;
  icon?: ReactNode;
  searchPlaceholder?: string;
  /** Shown when the list is empty (e.g. while options load, or none exist). */
  emptyMessage?: string;
  /** A disclosure above the list, e.g. the 3000-value truncation warning. */
  notice?: ReactNode;
  disabled?: boolean;
  className?: string;
  /**
   * `chip` (default): a toolbar Chip "Label value (count)". `field`: a
   * full-width form control showing only the value, for a form that already
   * prints the label above it (the trigger is still named "Label: value").
   */
  appearance?: 'chip' | 'field';
  /** Text for "nothing selected" (default "All"). */
  allLabel?: string;
  /** Called when the list opens or closes — e.g. to load options lazily on first open. */
  onOpenChange?: (open: boolean) => void;
  /** Offer the typed text as a value when no option matches it exactly (free entry). */
  creatable?: boolean;
};

/**
 * Pick several values from a searchable list. The trigger is a Chip showing
 * the field, the first selected label and a count badge. Keyboard: type to
 * filter, ↑↓ to move, Enter to toggle, Esc to close.
 */
export function MultiSelect({
  label,
  options,
  value,
  onChange,
  icon,
  searchPlaceholder,
  emptyMessage = 'No matches.',
  notice,
  disabled,
  className,
  appearance = 'chip',
  allLabel = 'All',
  onOpenChange,
  creatable = false,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selected = new Set(value);
  const firstLabel = options.find((o) => selected.has(o.value))?.label ?? value[0];
  const toggle = (v: string) => {
    onChange(selected.has(v) ? value.filter((x) => x !== v) : [...value, v]);
  };
  const changeOpen = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch('');
    onOpenChange?.(next);
  };
  const typed = search.trim();
  const canCreate = creatable && typed !== '' && !options.some((o) => o.value === typed || o.label === typed);
  const shown = value.length ? firstLabel : allLabel;

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        {appearance === 'field' ? (
          <button
            type="button"
            disabled={disabled}
            aria-label={`${label}: ${value.length > 1 ? `${String(shown)} and ${value.length - 1} more` : String(shown)}`}
            className={cn(
              'flex h-8 w-full min-w-0 items-center gap-1.5 rounded-ctl border border-line bg-surface px-2.5 text-left text-sm hover:border-line-strong aria-expanded:border-line-strong disabled:cursor-not-allowed disabled:opacity-45',
              className,
            )}
          >
            {icon ? <span aria-hidden="true" className="inline-flex [&_svg]:size-4">{icon}</span> : null}
            <span className={cn('min-w-0 flex-1 truncate', !value.length && 'text-muted')}>{shown}</span>
            {value.length > 1 ? (
              <span aria-hidden="true" className="num inline-grid h-[18px] min-w-[18px] place-items-center rounded-pill bg-blue px-[5px] text-2xs font-semibold text-on-blue">
                {value.length}
              </span>
            ) : null}
            <ChevronDown className="size-4 shrink-0 text-muted" aria-hidden="true" />
          </button>
        ) : (
          <Chip
            icon={icon}
            label={label}
            value={shown}
            count={value.length > 1 ? value.length : undefined}
            disabled={disabled}
            className={className}
          />
        )}
      </PopoverTrigger>
      <PopoverContent aria-label={label} className="p-0">
        <Command loop label={label}>
          <div className="flex items-center gap-2 border-b border-line px-3">
            <Search className="size-4 shrink-0 text-muted" aria-hidden="true" />
            <Command.Input
              value={search}
              onValueChange={setSearch}
              placeholder={searchPlaceholder ?? `Search ${label.toLowerCase()}…`}
              className="h-10 w-full border-0 bg-transparent text-sm outline-none placeholder:text-faint focus-visible:shadow-none"
            />
          </div>
          {notice ? <div className="border-b border-line px-3 py-2 text-xs text-muted">{notice}</div> : null}
          <Command.List className="max-h-[300px] overflow-y-auto p-1">
            {canCreate ? null : <Command.Empty className="px-3 py-6 text-center text-sm text-muted">{emptyMessage}</Command.Empty>}
            {canCreate ? (
              <Command.Item
                forceMount
                value={`__create__ ${typed}`}
                onSelect={() => { toggle(typed); setSearch(''); }}
                className="flex cursor-default items-center gap-2 rounded-ctl px-2 py-1.5 text-sm outline-none select-none data-[selected=true]:bg-wash"
              >
                <Plus className="size-4 shrink-0 text-muted" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">Add “{typed}”</span>
              </Command.Item>
            ) : null}
            {options.map((option) => {
              const isOn = selected.has(option.value);
              return (
                <Command.Item
                  key={option.value}
                  value={`${option.label} ${option.meta ?? ''} ${option.value}`}
                  onSelect={() => { toggle(option.value); }}
                  disabled={option.disabled === true}
                  data-checked={isOn || undefined}
                  className="flex cursor-default items-center gap-2 rounded-ctl px-2 py-1.5 text-sm outline-none select-none data-[disabled=true]:opacity-55 data-[selected=true]:bg-wash"
                >
                  <span
                    aria-hidden="true"
                    className={cn('grid size-4 shrink-0 place-items-center rounded-[4px] border', isOn ? 'border-fg bg-fg text-bg' : 'border-line-strong bg-surface')}
                  >
                    {isOn ? <Check className="size-3" strokeWidth={3} /> : null}
                  </span>
                  {option.icon}
                  <span className="min-w-0 flex-1 truncate">
                    {option.label}
                    {option.meta ? <span className="text-muted"> · {option.meta}</span> : null}
                  </span>
                  <span className="sr-only">{isOn ? ', selected' : ''}</span>
                </Command.Item>
              );
            })}
          </Command.List>
          {value.length ? (
            <div className="flex items-center justify-between border-t border-line px-3 py-2 text-xs text-muted">
              <span className="num">{value.length} selected</span>
              <button type="button" onClick={() => { onChange([]); }} className="border-0 bg-transparent p-0 font-medium text-fg hover:underline">
                Clear
              </button>
            </div>
          ) : null}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
